import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  where,
  type DocumentData,
} from "firebase/firestore";
import { db } from "@/firebaseConfig";
import type { Module, ActionContext } from "vuex";
import { Role, RoleDoc } from "@/models/interfaces";

export interface RolesState {
  accountId: number | null;
  items: Role[];
  byId: Record<string, {id: string, title:string}>
  fetching: boolean;
  error: string | null;
}

type RootState = unknown;

const state = (): RolesState => ({
  accountId: null,
  items: [],
  byId: {} as Record<string, {id: string, title: string}>,
  fetching: false,
  error: null,
});

const mutations = {
  SET_ACCOUNT_ID(s: RolesState, id: number | null) {
    s.accountId = id;
  },
  SET_FETCHING(s: RolesState, v: boolean) {
    s.fetching = v;
  },
  SET_ERROR(s: RolesState, msg: string | null) {
    s.error = msg;
  },
  SET_ITEMS(s: RolesState, items: Role[]) {
    s.items = items;
  },
  ADD_ITEM(s: RolesState, role: Role) {
    // Keep local list sorted by title
    s.items = [...s.items, role].sort((a, b) =>
      a.title.localeCompare(b.title, undefined, { sensitivity: "base" })
    );
  },
  UPSERT_ROLES(s: any, roles: Array<{ id: string; title: string }>) {
      for (const r of roles) s.byId[r.id] = r;
    },
  REMOVE_ITEM(s: RolesState, id: string) {
    s.items = s.items.filter((r) => r.id !== id);
  },
};

const actions = {
  setAccountId({ commit }: ActionContext<RolesState, RootState>, id: number | null) {
    commit("SET_ACCOUNT_ID", id);
  },

  async fetchAll({ state, commit }: ActionContext<RolesState, RootState>) {
    if (state.accountId == null) {
      commit("SET_ITEMS", []);
      return;
    }
    commit("SET_FETCHING", true);
    commit("SET_ERROR", null);
    try {
      const q = query(
        collection(db, "roles"),
        where("account", "==", state.accountId),
        orderBy("title")
      );
      const snap = await getDocs(q);
      const roles: Role[] = snap.docs.map((d) => {
        const data = d.data() as DocumentData;
        return {
          id: d.id,
          account: data.account as number,
          createdBy: data.createdBy as string,
          title: data.title as string,
        };
      });
      commit("SET_ITEMS", roles);
    } catch (err: any) {
      commit("SET_ERROR", err?.message ?? String(err));
    } finally {
      commit("SET_FETCHING", false);
    }
  },

  async fetchByIds({ commit }: ActionContext<RolesState, RootState>, ids: string[]) {
    const results: Array<{ id: string; title: string }> = [];
    for (const id of ids) {
      const r = await getDoc(doc(db, "roles", id));
      if (r.exists()) results.push({ id: r.id, title: r.data().title || "" });
    }
    commit("UPSERT_ROLES", results);
  },

  async addRole(
    { state, commit }: ActionContext<RolesState, RootState>,
    payload: { title: string; createdBy: string }
  ) {
    if (state.accountId == null) throw new Error("No accountId set");
    const docData: RoleDoc = {
      account: state.accountId,
      createdBy: payload.createdBy,
      title: payload.title.trim(),
    };
    const ref = await addDoc(collection(db, "roles"), docData);
    // Optimistic add to local state
    commit("ADD_ITEM", { id: ref.id, ...docData });
    return ref.id;
  },

  async deleteRole(
    { commit }: ActionContext<RolesState, RootState>,
    id: string
  ) {
    await deleteDoc(doc(db, "roles", id));
    commit("REMOVE_ITEM", id);
  },
};

const getters = {
  roles(state: RolesState): Role[] {
    return state.items;
  },
  byId: (s: any) => s.byId,
  byTitle:
    (state: RolesState) =>
    (title: string): Role | undefined =>
      state.items.find((r) => r.title.toLowerCase() === title.toLowerCase()),
  isFetching(state: RolesState): boolean {
    return state.fetching;
  },
  error(state: RolesState): string | null {
    return state.error;
  },
};

export const roles: Module<RolesState, RootState> = {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
export default roles;
